import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import { Shell } from "@/components/Shell";
import { ContactPicker } from "@/components/ContactPicker";
import { ChipGroup, Section, Counter } from "@/components/fields";
import { ProductGrid } from "@/components/ProductGrid";
import { CALL_STATUS, DOCTOR_RESPONSE, SAMPLE_TYPES, FOLLOW_UP } from "@/constants";
import { saveVisit } from "@/lib/offline";
import { getGps } from "@/lib/format";
import { toast } from "sonner";
import { VisitTimePicker } from "@/components/VisitTimePicker";
import { VisitWarningDialog } from "@/components/VisitWarningDialog";
import { validateVisitTime } from "@/lib/visitTime";
import { CaretLeft } from "@phosphor-icons/react";

const addDays = (n) => { const d = new Date(); d.setDate(d.getDate() + Number(n)); return d.toISOString().slice(0, 10); };

export default function DoctorVisit() {
  const { session } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [areas, setAreas] = useState([]);
  const [products, setProducts] = useState([]);
  const [contact, setContact] = useState(null);
  const [callStatus, setCallStatus] = useState("met_doctor");
  const [detailed, setDetailed] = useState([]);
  const [response, setResponse] = useState("");
  const [sampleType, setSampleType] = useState("nothing");
  const [sampleProduct, setSampleProduct] = useState("");
  const [sampleQty, setSampleQty] = useState(1);
  const [followUp, setFollowUp] = useState("none");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  const [visitTime, setVisitTime] = useState(() => new Date().toISOString());
  const [warnings, setWarnings] = useState([]);
  const [pendingNext, setPendingNext] = useState(false);
  const [todayVisits, setTodayVisits] = useState([]);
  const [maxBackdate, setMaxBackdate] = useState(720);

  useEffect(() => {
    api.get("/areas").then(({ data }) => setAreas(data));
    api.get("/products").then(({ data }) => setProducts(data));
    api.get("/visits", { params: { date: new Date().toISOString().slice(0, 10) } }).then(({ data }) => setTodayVisits(data));
    api.get("/settings").then(({ data }) => setMaxBackdate(data.max_backdate_minutes || 720));
  }, []);

  const met = ["met_doctor", "met_staff"].includes(callStatus);

  const reset = () => {
    setContact(null); setCallStatus("met_doctor"); setDetailed([]); setResponse("");
    setSampleType("nothing"); setSampleProduct(""); setSampleQty(1);
    setFollowUp("none"); setRemarks("");
    setVisitTime(new Date().toISOString()); setWarnings([]);
  };

  const doSave = async (next) => {
    setSaving(true);
    const gps = await getGps();
    const samples = sampleType === "sample" && sampleProduct
      ? [{ product_id: sampleProduct, qty: sampleQty }] : [];
    const payload = {
      id: crypto.randomUUID(), contact_type: "doctor", contact_id: contact.id,
      contact_name: contact.name, working_area: session?.working_area || contact.area,
      visit_time: visitTime, created_at: new Date().toISOString(),
      call_status: callStatus, products_detailed: met ? detailed : [],
      response, sample_type: sampleType, samples,
      follow_up_date: followUp !== "none" ? addDays(followUp) : null,
      remarks, gps,
    };
    const res = await saveVisit(payload);
    setSaving(false); setWarnings([]);
    toast.success(res.offline ? t("saved_offline") : t("save_visit"));
    if (next) reset(); else nav("/add-visit");
  };

  const save = (next) => {
    const v = validateVisitTime({ visitTime, sessionStart: session?.start_time,
      existingVisits: todayVisits, contactId: contact.id, maxBackdateMin: maxBackdate, t });
    if (v.block) { toast.error(v.block); return; }
    if (v.warnings.length) { setWarnings(v.warnings); setPendingNext(next); return; }
    doSave(next);
  };

  if (!contact) {
    return (
      <Shell title={t("doctor_visit")} nav={false}
        back={<button onClick={() => nav("/add-visit")} data-testid="back-btn"><CaretLeft size={22} /></button>}>
        <ContactPicker ctype="doctor" areas={areas} onSelect={setContact} />
      </Shell>
    );
  }

  return (
    <Shell title={contact.name} nav={false}
      back={<button onClick={() => setContact(null)} data-testid="back-btn"><CaretLeft size={22} /></button>}>
      <div className="space-y-4">
        <div className="rounded-xl bg-white border border-slate-200 p-4">
          <p className="font-head font-bold text-slate-800">{contact.name}</p>
          <p className="text-sm text-slate-500">{[contact.speciality, contact.clinic, contact.area].filter(Boolean).join(" · ")}</p>
          {contact.last_visit && <p className="text-xs text-slate-400 mt-1">{t("last_visit")}: {contact.last_visit} · {contact.last_response}</p>}
        </div>

        <VisitTimePicker value={visitTime} onChange={setVisitTime} />

        <Section title={t("call_status")} testid="section-callstatus">
          <ChipGroup options={CALL_STATUS} value={callStatus} onChange={setCallStatus} testid="call-status" />
        </Section>

        {met && (
          <>
            <Section title={t("products_detailed")} testid="section-products">
              <ProductGrid products={products} selected={detailed} onChange={setDetailed} />
            </Section>
            <Section title={t("doctor_response")} testid="section-response">
              <ChipGroup options={DOCTOR_RESPONSE} value={response} onChange={setResponse} testid="doctor-response" />
            </Section>
            <Section title={t("samples")} testid="section-samples">
              <ChipGroup options={SAMPLE_TYPES} value={sampleType} onChange={setSampleType} testid="sample-type" />
              {sampleType === "sample" && (
                <div className="mt-3 flex items-center justify-between gap-2">
                  <select value={sampleProduct} onChange={(e) => setSampleProduct(e.target.value)} data-testid="sample-product" className="flex-1 min-h-[44px] rounded-lg border border-slate-200 px-3 bg-white">
                    <option value="">--</option>
                    {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <Counter value={sampleQty} onChange={setSampleQty} min={1} testid="sample-qty" />
                </div>
              )}
            </Section>
          </>
        )}

        <Section title={t("follow_up")} testid="section-followup">
          <ChipGroup options={FOLLOW_UP} value={followUp} onChange={setFollowUp} testid="follow-up" />
        </Section>

        <Section title={t("remarks")} testid="section-remarks">
          <textarea data-testid="remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} className="w-full rounded-lg border border-slate-200 p-3" />
        </Section>

        <div className="flex gap-2">
          <button onClick={() => nav("/add-visit")} data-testid="cancel-btn" className="flex-1 min-h-[52px] rounded-lg border border-slate-300 font-semibold text-slate-600">{t("cancel")}</button>
          <button onClick={() => save(true)} disabled={saving} data-testid="save-next-btn" className="flex-1 min-h-[52px] rounded-lg bg-slate-800 text-white font-semibold">{t("save_next")}</button>
          <button onClick={() => save(false)} disabled={saving} data-testid="save-visit-btn" className="flex-1 min-h-[52px] rounded-lg bg-emerald-600 text-white font-semibold">{t("save_visit")}</button>
        </div>

        <VisitWarningDialog warnings={warnings} onCancel={() => setWarnings([])} onProceed={() => { setWarnings([]); doSave(pendingNext); }} />
      </div>
    </Shell>
  );
}
